import React, { useEffect, useRef, useState } from 'react';
import Sidebar from '../components/shared/Sidebar';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useThemeMode } from '../hooks/useThemeMode';
import OwnerTour from '../components/OwnerTour/OwnerTour';
import OwnerSetupGate from '../components/OwnerSetup/OwnerSetupGate';
import OwnerGettingStartedPanel from '../components/OwnerOnboarding/OwnerGettingStartedPanel';
import { useIsKioskOwner } from '../hooks/useCurrentRole';
import { AppRoutes } from '../types/constants.types';
import { FaChartLine, FaRegMoneyBillAlt, FaTv } from 'react-icons/fa';
import { SlEnergy } from 'react-icons/sl';
import { HiMenuAlt2, HiX } from 'react-icons/hi';
import Logo from '../components/shared/Logo';

const OWNER_MOBILE_LINKS = [
    { label: 'Performance', path: AppRoutes.KIOSK_PERFORMANCE, icon: <FaChartLine /> },
    { label: 'Kiosks', path: AppRoutes.KIOSKS, icon: <FaTv /> },
    { label: 'Rewards', path: AppRoutes.OWNER_REWARDS, icon: <FaRegMoneyBillAlt /> },
    { label: 'Support', path: AppRoutes.SUPPORT_REQUESTS, icon: <SlEnergy /> },
];

const OWNER_PAGE_LABELS: Record<string, string> = {
    [AppRoutes.KIOSK_PERFORMANCE]: 'Performance',
    [AppRoutes.KIOSKS]: 'Kiosks',
    [AppRoutes.OWNER_REWARDS]: 'Rewards',
    [AppRoutes.SUPPORT_REQUESTS]: 'Support',
    [AppRoutes.KIOSK_CONFIGURATION]: 'Kiosk CMS',
    [AppRoutes.OWNER_REPORTING_PAYOUT_SETTINGS]: 'Account Settings',
    [AppRoutes.SOCIAL_MEDIA_CAMPAIGN]: 'Social Media',
};

export default function DashboardLayout() {
    const { mode } = useThemeMode();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isKioskOwner = useIsKioskOwner();
    const { pathname } = useLocation();
    const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
    const mobileMenuPanelRef = useRef<HTMLElement>(null);
    const mobileMenuWasOpen = useRef(false);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [pathname]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const focusableElements = () => mobileMenuPanelRef.current?.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        focusableElements()?.[0]?.focus();
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMobileMenuOpen(false);
                return;
            }
            if (event.key !== 'Tab') return;

            const focusable = focusableElements();
            if (!focusable?.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (mobileMenuOpen) {
            mobileMenuWasOpen.current = true;
        } else if (mobileMenuWasOpen.current) {
            mobileMenuWasOpen.current = false;
            mobileMenuButtonRef.current?.focus();
        }
    }, [mobileMenuOpen]);

    const currentPageLabel = OWNER_PAGE_LABELS[pathname] || 'Kiosk Portal';

  return (
            <div className={`flex flex-row h-screen w-screen overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#f8fbf9] via-[#eff5f2] to-[#e8efeb] dark:from-[#121a17] dark:via-[#101816] dark:to-[#0c1210] transition-colors duration-300 ${isKioskOwner ? 'owner-mobile-shell' : ''}`}>
                <aside
                    data-tour="sidebar"
                    className={`${isKioskOwner ? 'hidden md:block ' : ''}h-screen p-3 flex-shrink-0 transition-all duration-300 ease-in-out ${collapsed ? 'w-[76px]' : 'w-[272px]'}`}
                >
                        <div className='h-full rounded-3xl border border-white/50 dark:border-white/10 bg-white/45 dark:bg-slate-900/45 backdrop-blur-xl shadow-sm overflow-hidden'>
                            <Sidebar mode={mode} collapsed={collapsed} onToggleCollapse={() => setCollapsed(c => !c)} />
                        </div>
                </aside>

                {isKioskOwner && (
                    <header className="owner-mobile-header md:hidden">
                        <button
                            ref={mobileMenuButtonRef}
                            type="button"
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label="Open navigation menu"
                            aria-expanded={mobileMenuOpen}
                            aria-controls="owner-mobile-navigation"
                            className="owner-mobile-menu-button"
                        >
                            <HiMenuAlt2 aria-hidden="true" />
                        </button>
                        <div className="flex min-w-0 items-center gap-2">
                            <Logo mode={mode} variant="icon" className="h-8 w-8 shrink-0" />
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Kiosk Portal</p>
                                <p className="truncate text-xs text-gray-500 dark:text-gray-400">{currentPageLabel}</p>
                            </div>
                        </div>
                        <span className="w-10" aria-hidden="true" />
                    </header>
                )}

                {isKioskOwner && mobileMenuOpen && (
                    <div className="owner-mobile-menu md:hidden">
                        <button
                            type="button"
                            className="owner-mobile-menu-backdrop"
                            onClick={() => setMobileMenuOpen(false)}
                            aria-label="Close navigation menu"
                        />
                        <aside
                            ref={mobileMenuPanelRef}
                            id="owner-mobile-navigation"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Kiosk owner navigation"
                            className="owner-mobile-menu-panel"
                        >
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                aria-label="Close navigation menu"
                                className="owner-mobile-menu-close"
                            >
                                <HiX aria-hidden="true" />
                            </button>
                            <div className="h-full overflow-hidden rounded-3xl border border-white/50 bg-white/90 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95">
                                <Sidebar mode={mode} collapsed={false} onToggleCollapse={() => undefined} hideCollapseToggle />
                            </div>
                        </aside>
                    </div>
                )}

          <main className={`flex flex-1 min-w-0 h-screen flex-col overflow-hidden ${isKioskOwner ? 'owner-mobile-main p-0 md:p-4 md:pl-0' : 'p-4 pl-0'}`}>
              <div className={`flex min-h-0 flex-1 rounded-3xl border border-white/50 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl shadow-sm overflow-hidden ${isKioskOwner ? 'owner-mobile-surface rounded-none border-0 md:rounded-3xl md:border' : ''}`}>
                <div className={`min-h-0 flex-1 overflow-auto ${isKioskOwner ? 'owner-mobile-content' : ''}`}>
                  <Outlet />
                </div>
                                </div>
                </main>
                {isKioskOwner && (
                    <nav className="owner-mobile-bottom-nav md:hidden" aria-label="Primary navigation">
                        {OWNER_MOBILE_LINKS.map((item) => {
                            const isActive = pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`owner-mobile-bottom-link${isActive ? ' is-active' : ''}`}
                                >
                                    <span aria-hidden="true">{item.icon}</span>
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>
                )}
            <OwnerGettingStartedPanel />
            <OwnerSetupGate />
            <OwnerTour />
    </div>
  )
}
